<template>
  <div class="space-y-6">
    <!-- 页面标题 -->
    <div>
      <h2 class="text-2xl font-semibold text-gray-700">游戏月度报表</h2>
      <div class="flex items-center mt-2">
        <router-link to="/admin/dashboard" class="text-gray-600 hover:text-gray-800">首页</router-link>
        <span class="mx-2 text-gray-400">•</span>
        <router-link to="/admin/bets" class="text-gray-600 hover:text-gray-800">注单管理</router-link>
        <span class="mx-2 text-gray-400">•</span>
        <span class="text-gray-600">月度报表</span>
      </div>
    </div>
    
    <!-- 筛选器 -->
    <SearchFilters
      :filter-config="filterConfig"
      :initial-filters="filters"
      @search="search"
      @reset="resetFilters"
    />
    
    <!-- 整体统计卡片 -->
    <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
      <div class="bg-white rounded-lg shadow p-6">
        <div class="flex items-center">
          <div class="p-3 rounded-full bg-blue-100 text-blue-600">
            <i class="fas fa-money-bill-wave text-xl"></i>
          </div>
          <div class="ml-4 overflow-hidden">
            <p class="text-sm font-medium text-gray-500">总投注额</p>
            <p class="text-xl font-semibold text-gray-800 truncate" 
               :title="`${filters.currency} ${summary.totalBets.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`">
              {{ formatMoney(summary.totalBets, 0) }}
            </p>
          </div>
        </div>
        <div class="mt-4">
          <div class="flex items-center justify-between">
            <p class="text-sm text-gray-500">{{ summary.comparisonPeriod || '环比' }}</p>
            <p :class="['text-sm font-semibold', getChangeClass(summary.betChange)]">
              {{ formatChange(summary.betChange) }}
            </p>
          </div>
        </div>
      </div>
      
      <div class="bg-white rounded-lg shadow p-6">
        <div class="flex items-center">
          <div class="p-3 rounded-full bg-green-100 text-green-600">
            <i class="fas fa-chart-pie text-xl"></i>
          </div>
          <div class="ml-4 overflow-hidden">
            <p class="text-sm font-medium text-gray-500">平台盈利</p>
            <p class="text-xl font-semibold text-gray-800 truncate"
               :title="`${filters.currency} ${summary.totalProfit.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`">
              {{ formatMoney(summary.totalProfit, 0) }}
            </p>
          </div>
        </div>
        <div class="mt-4">
          <div class="flex items-center justify-between">
            <p class="text-sm text-gray-500">{{ summary.comparisonPeriod || '环比' }}</p>
            <p :class="['text-sm font-semibold', getChangeClass(summary.profitChange)]">
              {{ formatChange(summary.profitChange) }}
            </p>
          </div>
        </div>
      </div>
      
      <div class="bg-white rounded-lg shadow p-6">
        <div class="flex items-center">
          <div class="p-3 rounded-full bg-purple-100 text-purple-600">
            <i class="fas fa-percentage text-xl"></i>
          </div>
          <div class="ml-4 overflow-hidden">
            <p class="text-sm font-medium text-gray-500">平均盈利率</p>
            <p class="text-xl font-semibold text-gray-800 truncate" :title="`${summary.profitRate}%`">
              {{ formatPercent(summary.profitRate) }}
            </p>
          </div>
        </div>
        <div class="mt-4">
          <div class="flex items-center justify-between">
            <p class="text-sm text-gray-500">{{ summary.comparisonPeriod || '环比' }}</p>
            <p :class="['text-sm font-semibold', getChangeClass(summary.rateChange)]">
              {{ formatChangePercent(summary.rateChange) }}
            </p>
          </div>
        </div>
      </div>
      
      <div class="bg-white rounded-lg shadow p-6">
        <div class="flex items-center">
          <div class="p-3 rounded-full bg-yellow-100 text-yellow-600">
            <i class="fas fa-users text-xl"></i>
          </div>
          <div class="ml-4 overflow-hidden">
            <p class="text-sm font-medium text-gray-500">活跃玩家</p>
            <p class="text-xl font-semibold text-gray-800 truncate" :title="`${summary.activePlayers.toLocaleString()}`">
              {{ formatNumber(summary.activePlayers) }}
            </p>
          </div>
        </div>
        <div class="mt-4">
          <div class="flex items-center justify-between">
            <p class="text-sm text-gray-500">{{ summary.comparisonPeriod || '环比' }}</p>
            <p :class="['text-sm font-semibold', getChangeClass(summary.playerChange)]">
              {{ formatChange(summary.playerChange) }}
            </p>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 图表区域 -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <!-- 日投注趋势图 -->
      <div class="bg-white rounded-lg shadow p-6">
        <h3 class="text-lg font-medium text-gray-700 mb-4">每日投注趋势</h3>
        <div v-if="loading" class="flex justify-center items-center h-64">
          <div class="loader"></div>
        </div>
        <div v-else class="h-64" ref="dailyTrendsChart"></div>
      </div>
      
      <!-- 游戏类型分布图 -->
      <div class="bg-white rounded-lg shadow p-6">
        <h3 class="text-lg font-medium text-gray-700 mb-4">游戏类型分布</h3>
        <div v-if="loading" class="flex justify-center items-center h-64">
          <div class="loader"></div>
        </div>
        <div v-else class="h-64" ref="gameDistributionChart"></div>
      </div>
    </div>
    
    <!-- 表格数据 -->
    <div class="bg-white shadow rounded-md overflow-hidden">
      <div class="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center">
        <h3 class="text-lg leading-6 font-medium text-gray-900">游戏表现详情</h3>
        <button @click="exportData" class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-500 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
          <i class="fas fa-download mr-2"></i> 导出数据
        </button>
      </div>
      
      <TableList
        :columns="columns"
        :items="gameData"
        :loading="loading"
        :total-items="totalItems"
        :current-page="currentPage"
        :items-per-page="1000"
        empty-text="暂无报表数据"
        @page-change="handlePageChange"
      >
        <!-- 游戏名称 -->
        <template #cell-game="{ item }">
          <div class="flex items-center">
            <div class="flex-shrink-0 h-10 w-10 rounded-md bg-gray-200 flex items-center justify-center">
              <i :class="['fas', getGameIcon(item.game_category)]"></i>
            </div>
            <div class="ml-4">
              <div class="text-sm font-medium text-gray-900">{{ item.game }}</div>
              <!-- <div class="text-sm text-gray-500">{{ getGameCategoryName(item.game_category) }}</div> -->
            </div>
          </div>
        </template>
        
        <!-- 投注金额 -->
        <template #cell-bet_amount="{ item }">
          <div class="text-sm font-medium text-gray-900 whitespace-nowrap" 
               :title="`${filters.currency} ${item.bet_amount}`">
            {{ item.bet_amount }}
          </div>
          <div v-if="filters.compare_mode !== 'none'" class="text-xs mt-1">
            <span :class="getChangeClass(item.bet_change)">
              {{ formatChange(item.bet_change) }}
            </span>
          </div>
        </template>
        
        <!-- 有效投注 -->
        <template #cell-valid_bet_amount="{ item }">
          <div class="text-sm font-medium text-gray-900 whitespace-nowrap"
               :title="`${filters.currency} ${item.valid_bet_amount}`">
            {{ item.valid_bet_amount }}
          </div>
        </template>
        
        <!-- 平台盈利 -->
        <template #cell-profit="{ item }">
          <div class="text-sm font-medium text-gray-900 whitespace-nowrap"
               :title="`${filters.currency} ${item.profit}`">
            {{ item.profit }}
          </div>
          <div v-if="filters.compare_mode !== 'none'" class="text-xs mt-1">
            <span :class="getChangeClass(item.profit_change)">
              {{ formatChange(item.profit_change) }}
            </span>
          </div>
        </template>
        
        <!-- 盈利率 -->
        <template #cell-profit_rate="{ item }">
          <div :class="['text-sm font-medium', getProfitRateClass(item.profit_rate)]">
            {{ formatPercent(item.profit_rate) }}
          </div>
          <div v-if="filters.compare_mode !== 'none'" class="text-xs mt-1">
            <span :class="getChangeClass(item.rate_change)">
              {{ formatChange(item.rate_change) }}%
            </span>
          </div>
        </template>
        
        <!-- 玩家数量 -->
        <template #cell-player_count="{ item }">
          <div class="text-sm font-medium text-gray-900">{{ formatNumber(item.player_count) }}</div>
          <div v-if="filters.compare_mode !== 'none'" class="text-xs mt-1">
            <span :class="getChangeClass(item.player_change)">
              {{ formatChange(item.player_change) }}
            </span>
          </div>
        </template>
        
        <!-- ARPU (平均每用户收入) -->
        <template #cell-arpu="{ item }">
          <div class="text-sm font-medium text-gray-900 whitespace-nowrap"
               :title="`${filters.currency} ${item.arpu}`">
            {{ item.arpu }}
          </div>
        </template>
      </TableList>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, nextTick } from 'vue';
import SearchFilters from '@/components/common/SearchFilters.vue';
import TableList from '@/components/common/TableList.vue';
import AdminApiClient from '@/utils/admin-api-client';

const apiClient = new AdminApiClient();

// 状态变量
const loading = ref(false);
const gameData = ref([]);
const totalItems = ref(0);
const currentPage = ref(1);
const itemsPerPage = ref(10);

// 图表引用
const dailyTrendsChart = ref(null);
const gameDistributionChart = ref(null);
const echartsLoaded = ref(false);

// 筛选条件
const filters = reactive({
  month: new Date().toISOString().slice(0, 7), // 当前月份 YYYY-MM
  game: '',
  currency: 'HKD',
  game_category: '',
  merchant_id: '1',
  compare_mode: 'previous',
  sort_by: 'bet_amount'
});

// 筛选器配置
const filterConfig = reactive([
  {
    id: 'month',
    type: 'month',
    label: '选择月份',
    placeholder: '选择月份',
    icon: 'calendar'
  },
  {
    id: 'game',
    type: 'input',
    label: '游戏',
    placeholder: '输入游戏名称',
    icon: 'gamepad'
  },
  {
    id: 'currency',
    type: 'select',
    label: '货币',
    placeholder: '选择货币',
    icon: 'money-bill-wave',
    options: [
      { value: 'HKD', label: 'HKD' },
      { value: 'USD', label: 'USD' },
      { value: 'CNY', label: 'CNY' }
    ]
  },
  {
    id: 'game_category',
    type: 'select',
    label: '游戏类型',
    placeholder: '选择游戏类型',
    icon: 'dice',
    options: [
      { value: '', label: '全部类型' },
      { value: 'slot', label: '老虎机' },
      { value: 'live', label: '真人' },
      { value: 'sport', label: '体育' }
    ]
  },
  {
    id: 'merchant_id',
    type: 'select',
    label: '商户',
    placeholder: '选择商户',
    icon: 'store',
    options: [
      { value: '1', label: '默认商户' }
    ]
  },
  {
    id: 'compare_mode',
    type: 'select',
    label: '比较模式',
    placeholder: '选择比较模式',
    icon: 'exchange-alt',
    options: [
      { value: 'previous', label: '与上月比较' },
      { value: 'year', label: '与去年同期比较' },
      { value: 'none', label: '不比较' }
    ]
  },
  {
    id: 'sort_by',
    type: 'select',
    label: '排序方式',
    placeholder: '选择排序方式',
    icon: 'sort',
    options: [
      { value: 'bet_amount', label: '按投注金额' },
      { value: 'profit', label: '按盈利额' },
      { value: 'profit_rate', label: '按盈利率' },
      { value: 'player_count', label: '按玩家数量' }
    ]
  }
]);

// 表格列配置
const columns = [
  { key: 'game', label: '游戏名称', sortable: true },
  { key: 'bet_amount', label: '投注金额', sortable: true },
  { key: 'valid_bet_amount', label: '有效投注', sortable: true },
  { key: 'profit', label: '平台盈利', sortable: true },
  { key: 'profit_rate', label: '盈利率', sortable: true },
  { key: 'player_count', label: '玩家数量', sortable: true },
  { key: 'arpu', label: '平均ARPU', sortable: true }
];

// 汇总数据
const summary = reactive({
  totalBets: 0,
  totalProfit: 0,
  profitRate: 0,
  activePlayers: 0,
  betChange: 0,
  profitChange: 0,
  rateChange: 0,
  playerChange: 0,
  comparisonPeriod: '环比'
});

// 动态加载ECharts库
function loadEcharts() {
  if (window.echarts) {
    echartsLoaded.value = true;
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/echarts@5.4.2/dist/echarts.min.js';
    script.onload = () => {
      echartsLoaded.value = true;
      console.log('ECharts库加载成功');
      resolve();
    };
    script.onerror = () => {
      console.error('加载ECharts库失败');
      reject(new Error('加载ECharts库失败'));
    };
    document.head.appendChild(script);
  });
}

// 搜索方法
function search(newFilters) {
  Object.assign(filters, newFilters);
  loadReportData();
}

// 重置筛选条件
function resetFilters() {
  filters.month = new Date().toISOString().slice(0, 7);
  filters.game = '';
  filters.currency = 'HKD';
  filters.game_category = '';
  filters.merchant_id = '1';
  filters.compare_mode = 'previous';
  filters.sort_by = 'bet_amount';
  
  loadReportData();
}

// 加载报表数据
function loadReportData(page = 1) {
  loading.value = true;
  currentPage.value = page;
  
  // 准备API请求参数
  const params = {
    ...filters,
    page,
    per_page: itemsPerPage.value
  };
  
  // 调用API获取数据
  apiClient.getReportsData(params)
    .then(response => {
      if (!response.success) {
        console.error('API请求成功但返回失败状态:', response.message);
        loading.value = false;
        return;
      }
      
      const data = response.data || {};
      const reportData = data.reportData || {};
      
      // 更新汇总数据
      if (reportData.stats) {
        summary.totalBets = reportData.stats.bet_amount || 0;
        summary.totalProfit = reportData.stats.profit || 0;
        summary.profitRate = parseFloat(reportData.stats.profit_rate) || 0;
        summary.activePlayers = reportData.stats.unique_players || 0;
        summary.betChange = reportData.stats.bet_amount_change || 0;
        summary.profitChange = reportData.stats.profit_change || 0;
        summary.rateChange = 0; // API没有提供，设为0
        summary.playerChange = reportData.stats.player_count_change || 0;
        summary.comparisonPeriod = reportData.stats.comparison_period || '环比';
      }
      
      // 更新表格数据
      if (reportData.game_profit_contribution && reportData.game_profit_contribution.games) {
        // 调试输出
        console.log('游戏数据:', reportData.game_profit_contribution.games);
        
        // 修正：将games数组赋值给gameData.value
        let gamesList = []
        gamesList = reportData.game_profit_contribution.games;
        gamesList.push(reportData.game_profit_contribution.totals)
        gameData.value = gamesList.map(game => ({
          game: game.game_name || '未知游戏',
          game_category: game.game_type || '',
          game_code: game.game_code || '',
          bet_amount: game.bet_amount || 0,
          valid_bet_amount: game.valid_bet_amount || 0,
          profit: game.profit || 0,
          profit_rate: game.profit_rate ? parseFloat(game.profit_rate) : 0,
          player_count: game.player_count || 0,
          arpu: game.bet_amount && game.player_count ? game.bet_amount / game.player_count : 0,
          bet_change: game.bet_amount_change || 0,
          profit_change: game.profit_change || 0,
          rate_change: 0, // API没有提供，设为0
          player_change: game.player_count_change || 0
        }));
        totalItems.value = gameData.value.length || 0;
      } else {
        // 设置为空数组
        reportData.value = [];
        totalItems.value = 0;
      }
      
      // 加载图表
      if (reportData.trend_data) {
        const trendsData = {
          days: reportData.trend_data.labels || [],
          bet_amounts: reportData.trend_data.bet_data || [],
          profit_amounts: reportData.trend_data.win_data.map((win, index) => {
            const bet = reportData.trend_data.bet_data[index] || 0;
            // 修正计算方式：profit = bet - win (利润 = 投注 - 赢钱)
            return parseFloat(bet) - parseFloat(win);
          })
        };
        
        // 确保ECharts已加载
        loadEcharts().then(() => {
          nextTick(() => {
            renderDailyTrendsChart(trendsData);
            
            if (reportData.game_distribution) {
              const distributionData = reportData.game_distribution.labels.map((label, index) => ({
                category: label,
                amount: reportData.game_distribution.data[index] || 0
              }));
              
              renderGameDistributionChart(distributionData);
            }
          });
        }).catch(error => {
          console.error('无法加载图表:', error);
          // 显示错误提示
          if (dailyTrendsChart.value) {
            dailyTrendsChart.value.innerHTML = '<div class="flex items-center justify-center h-full"><p class="text-red-500">加载图表库失败，请刷新页面重试</p></div>';
          }
          if (gameDistributionChart.value) {
            gameDistributionChart.value.innerHTML = '<div class="flex items-center justify-center h-full"><p class="text-red-500">加载图表库失败，请刷新页面重试</p></div>';
          }
        });
      } else if (reportData.game_distribution) {
        // 确保ECharts已加载
        loadEcharts().then(() => {
          nextTick(() => {
            const distributionData = reportData.game_distribution.labels.map((label, index) => ({
              category: label,
              amount: reportData.game_distribution.data[index] || 0
            }));
            
            renderGameDistributionChart(distributionData);
          });
        }).catch(error => {
          console.error('无法加载图表:', error);
          if (gameDistributionChart.value) {
            gameDistributionChart.value.innerHTML = '<div class="flex items-center justify-center h-full"><p class="text-red-500">加载图表库失败，请刷新页面重试</p></div>';
          }
        });
      }
      
      // 保存游戏类型列表用于过滤
      if (data.gameTypes && data.gameTypes.length > 0) {
        filterConfig.find(f => f.id === 'game_category').options = [
          { value: '', label: '全部类型' },
          ...data.gameTypes.map(type => ({ 
            value: type, 
            label: getGameCategoryName(type) || type 
          }))
        ];
      }
      
      loading.value = false;
    })
    .catch(error => {
      console.error('加载报表数据失败:', error);
      loading.value = false;
    });
}

// 修改渲染每日趋势图函数
function renderDailyTrendsChart(trendsData = null) {
  // 防止在echarts未加载时尝试初始化图表
  if (!window.echarts || !dailyTrendsChart.value) {
    console.error('ECharts未加载或图表容器不存在');
    if (dailyTrendsChart.value) {
      dailyTrendsChart.value.innerHTML = '<div class="flex items-center justify-center h-full"><p class="text-red-500">图表初始化失败，请刷新页面重试</p></div>';
    }
    return;
  }
  
  try {
    // 使用API数据或默认空数据
    const chartData = trendsData || { days: [], bet_amounts: [], profit_amounts: [] };
    
    // 创建图表实例
    const chart = window.echarts.init(dailyTrendsChart.value);
    
    // 配置图表选项
    const option = {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        },
        formatter: function(params) {
          let result = `${params[0].name}<br/>`;
          
          // 遍历每个系列的数据
          params.forEach(param => {
            const value = param.value;
            // 为大数值添加千位分隔符并显示完整数值
            const formattedValue = value.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            });
            result += `${param.seriesName}: ${filters.currency} ${formattedValue}<br/>`;
          });
          
          return result;
        }
      },
      legend: {
        data: ['投注额', '盈利额'],
        top: 0,
        right: 0
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: chartData.days || [],
        axisLabel: {
          interval: 'auto',
          rotate: 45,
          formatter: function(value) {
            // 截断日期，只显示月日
            if (value && value.length >= 10) {
              return value.substring(5); // 只显示MM-DD部分
            }
            return value;
          }
        }
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: (value) => {
            if (value >= 1000000000) {
              return (value / 1000000000).toFixed(1) + 'B';
            } else if (value >= 1000000) {
              return (value / 1000000).toFixed(1) + 'M';
            } else if (value >= 1000) {
              return (value / 1000).toFixed(1) + 'K';
            }
            return value;
          }
        }
      },
      series: [
        {
          name: '投注额',
          type: 'bar',
          itemStyle: {
            color: '#4f46e5'
          },
          data: chartData.bet_amounts || []
        },
        {
          name: '盈利额',
          type: 'line',
          itemStyle: {
            color: '#10b981'
          },
          data: chartData.profit_amounts || []
        }
      ]
    };
    
    // 渲染图表
    chart.setOption(option);
    
    // 监听窗口大小变化，调整图表大小
    window.addEventListener('resize', () => {
      chart.resize();
    });
  } catch (error) {
    console.error('渲染每日趋势图失败:', error);
    if (dailyTrendsChart.value) {
      dailyTrendsChart.value.innerHTML = '<div class="flex items-center justify-center h-full"><p class="text-red-500">图表渲染失败: ' + error.message + '</p></div>';
    }
  }
}

// 修改渲染游戏类型分布图函数
function renderGameDistributionChart(distributionData = null) {
  // 防止在echarts未加载时尝试初始化图表
  if (!window.echarts || !gameDistributionChart.value) {
    console.error('ECharts未加载或图表容器不存在');
    if (gameDistributionChart.value) {
      gameDistributionChart.value.innerHTML = '<div class="flex items-center justify-center h-full"><p class="text-red-500">图表初始化失败，请刷新页面重试</p></div>';
    }
    return;
  }
  
  try {
    // 创建图表实例
    const chart = window.echarts.init(gameDistributionChart.value);
    
    // 使用API数据或默认空数据
    const data = distributionData || [];
    
    // 从API获取的数据转换为echarts需要的格式
    const chartData = data.map(item => ({
      value: item.amount || 0,
      name: item.category
    }));
    
    // 配置图表选项
    const option = {
      tooltip: {
        trigger: 'item',
        formatter: function(params) {
          // 使用千分位分隔符格式化金额
          const value = params.value.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          });
          return `${params.name}<br/>${params.seriesName}: ${filters.currency} ${value} (${params.percent}%)`;
        }
      },
      legend: {
        type: 'scroll',
        orient: 'vertical',
        right: 0,
        top: 'center',
        data: chartData.map(item => item.name),
        formatter: function(name) {
          // 截断长名称
          if (name.length > 12) {
            return name.substring(0, 10) + '...';
          }
          return name;
        }
      },
      series: [
        {
          name: '游戏投注',
          type: 'pie',
          radius: ['50%', '70%'],
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 10,
            borderColor: '#fff',
            borderWidth: 2
          },
          label: {
            show: false,
            position: 'center'
          },
          emphasis: {
            label: {
              show: true,
              fontSize: '16',
              fontWeight: 'bold'
            }
          },
          labelLine: {
            show: false
          },
          data: chartData,
          color: ['#4f46e5', '#10b981', '#f59e0b', '#6b7280', '#ec4899', '#8b5cf6', '#0ea5e9', '#ef4444', '#14b8a6', '#f97316']
        }
      ]
    };
    
    // 渲染图表
    chart.setOption(option);
    
    // 监听窗口大小变化，调整图表大小
    window.addEventListener('resize', () => {
      chart.resize();
    });
  } catch (error) {
    console.error('渲染游戏类型分布图失败:', error);
    if (gameDistributionChart.value) {
      gameDistributionChart.value.innerHTML = '<div class="flex items-center justify-center h-full"><p class="text-red-500">图表渲染失败: ' + error.message + '</p></div>';
    }
  }
}

// 处理页码变化
function handlePageChange(page) {
  loadReportData(page);
}

// 导出数据
function exportData() {
  // 构建导出URL
  const params = new URLSearchParams();
  Object.keys(filters).forEach(key => {
    if (filters[key]) {
      params.append(key, filters[key]);
    }
  });
  
  const exportUrl = `/admin/api/reports/export?${params.toString()}`;
  window.open(exportUrl, '_blank');
}

// 格式化方法
function formatMoney(value, fractionDigits = 2) {
  // 对于大数字进行简化显示
  if (Math.abs(value) >= 1000000000) {
    return `${filters.currency} ${(value / 1000000000).toFixed(1)}B`;
  } else if (Math.abs(value) >= 1000000) {
    return `${filters.currency} ${(value / 1000000).toFixed(1)}M`;
  } else if (Math.abs(value) >= 1000) {
    return `${filters.currency} ${(value / 1000).toFixed(1)}K`;
  }
  
  return `${filters.currency} ${Number(value).toLocaleString(undefined, { 
    minimumFractionDigits: fractionDigits, 
    maximumFractionDigits: fractionDigits 
  })}`;
}

function formatPercent(value) {
  return `${Number(value).toFixed(2)}%`;
}

function formatNumber(value) {
  // 对于大数字进行简化显示
  if (Math.abs(value) >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`;
  } else if (Math.abs(value) >= 1000) {
    return `${(value / 1000).toFixed(1)}K`;
  }
  
  return Number(value).toLocaleString();
}

function formatChange(value) {
  const sign = value >= 0 ? '+' : '';
  return `${sign}${Number(value).toFixed(2)}%`;
}

function formatChangePercent(value) {
  const sign = value >= 0 ? '+' : '';
  return `${sign}${Number(value).toFixed(2)}个百分点`;
}

// 获取样式类
function getChangeClass(value) {
  return value >= 0 ? 'text-green-600' : 'text-red-600';
}

function getProfitRateClass(value) {
  if (value >= 20) return 'text-green-600';
  if (value >= 0) return 'text-blue-600';
  return 'text-red-600';
}

// 获取游戏图标
function getGameIcon(category) {
  const icons = {
    'sport': 'fa-futbol',
    'casino': 'fa-dice',
    'slot': 'fa-slot-machine',
    'lottery': 'fa-ticket-alt',
    'fishing': 'fa-fish',
    'animal': 'fa-horse',
    'chess': 'fa-chess',
    'esport': 'fa-gamepad',
    'egame': 'fa-laptop-code',
    'qgame': 'fa-arcade',
    'arcade': 'fa-arcade',
    'default': 'fa-gamepad'
  };
  return icons[category] || icons.default;
}

// 获取游戏类型名称
function getGameCategoryName(category) {
  // 根据API返回的游戏类型代码映射到中文名称
  const names = {
    'sport': '体育',
    'casino': '真人',
    'slot': '老虎机',
    'lottery': '彩票',
    'fishing': '捕鱼',
    'animal': '动物竞技',
    'chess': '棋牌',
    'esport': '电竞',
    'egame': '电子游戏',
    'qgame': '街机',
    'arcade': '街机',
    'default': '其他游戏'
  };
  return names[category] || names.default;
}

// 新增一个更简洁的金额格式化方法用于表格显示
function formatCompactMoney(value) {
  // 对于大数字进行简化显示，不显示货币符号
  if (Math.abs(value) >= 1000000000) {
    return `${(value / 1000000000).toFixed(1)}B`;
  } else if (Math.abs(value) >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`;
  } else if (Math.abs(value) >= 1000) {
    return `${(value / 1000).toFixed(1)}K`;
  }
  
  return Number(value).toFixed(2);
}

// 页面加载时初始化
onMounted(() => {
  // 尝试加载ECharts库
  loadEcharts().catch(error => {
    console.error('初始化时加载ECharts库失败:', error);
  });
  
  // 加载报表数据
  loadReportData();
});
</script>

<style scoped>
.loader {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style> 